import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useMutation } from '@tanstack/react-query';
import { Urls } from 'data';
import { Storage, validateEmail, formatDate } from 'helpers';
import { useAPI } from 'hooks';
import Locales from 'locales';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Button } from 'components/ui/button';
import { Alert, AlertDescription } from 'components/ui/alert';
import { Loader2, Eye, EyeOff, TriangleAlert } from 'lucide-react';
import { AuthCard, AuthPageLayout } from 'components/shared/AuthPageLayout';
import LegalNotice from './components/LegalNotice';
import OTPForm from './components/OTPForm';

const CONST_USER = 'user';
const CONST_LOGIN = 'login';
const CONST_OTP = 'otp';
const CONST_USER_TOKEN = 'user_token';
const CONST_LEGAL_ACCEPTED = 'legal_accepted';
const CONST_LEGAL_DOWNLOADED = 'legal_downloaded';
const CONST_LEGAL_ACCEPTANCE_DATE = 'legal_acceptance_date';
const RESEND_OTP_TIMER_SECONDS = 180;

const headers = {
  'Content-Type': 'application/json',
  'Access-Control-Allow-Origin': '*',
  'Access-Control-Allow-Headers': 'Content-Type, Authorization',
};

const Login = () => {
  const { api } = useAPI();

  // Form States
  const [email, setEmail] = useState('');
  const [pwd, setPwd] = useState('');
  const [otpValue, setOtpValue] = useState('');
  const [error, setError] = useState('');

  // UI States
  const [displayPwd, setDisplayPwd] = useState(false);
  const [step, setStep] = useState(CONST_LOGIN); // 'login' or 'otp'
  const [legalNoticeOpen, setLegalNoticeOpen] = useState(true);
  const [resendTimer, setResendTimer] = useState(0);

  const storage = new Storage(localStorage, 'eHadj-');

  const isEmptyField = email.trim() === '' || pwd.trim() === '';
  const isLegalAccepted = storage.getData(CONST_LEGAL_ACCEPTED) === true;
  const isLegalDownloaded = storage.getData(CONST_LEGAL_DOWNLOADED) === true;
  const isLoginStep = step === CONST_LOGIN;

  const title = isLoginStep ? Locales.auth.login : Locales.auth.otp;

  const description = isLoginStep
    ? Locales.auth.login_description
    : Locales.formatString(Locales.auth.otp_description, {
        email: <strong>{email}</strong>,
      });

  const downloadConsentReceipt = () => {
    if (!isLegalDownloaded) {
      const pdfUrl = `${process.env.PUBLIC_URL}pdfs/legal-consent.pdf`;
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = Locales.base.legals.document_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      storage.set(CONST_LEGAL_DOWNLOADED, true);
    }
  };

  // 1. First Step: Login Mutation
  const { mutate: logUser, isLoading: isLoggingIn } = useMutation({
    mutationFn: () => api.post('auth/user_token/', { email, password: pwd }, { headers }),
    onSuccess: ({ data }) => {
      toast.success(data.message);
      setError('');
      setStep(CONST_OTP); // Switch to OTP UI
      startTimer();
    },
    onError: (error) => {
      setError(error || Locales.auth.errors.invalid_identifiers);
    },
  });

  // 2. Second Step: OTP Validation
  const { mutate: verifyOTP, isLoading: isVerifyingOTP } = useMutation({
    mutationFn: () => api.post('auth/user_token/verify_otp/', { email, otp: otpValue }, { headers }),
    onSuccess: ({ data }) => {
      // Finalize login
      storage.set(CONST_USER_TOKEN, data.auth_key);
      storage.set(CONST_USER, JSON.stringify(data.user));
      downloadConsentReceipt();
      window.location = Urls.base;
    },
    onError: () => {
      setError('Code OTP invalide ou expiré.');
      setOtpValue('');
    },
  });

  // Resend OTP Mutation
  const { mutate: resendOTP, isLoading: isResending } = useMutation({
    mutationFn: () => api.post('auth/user_token/', { email, password: pwd }, { headers }),
    onSuccess: () => {
      toast.success('Un nouveau code a été envoyé.');
      startTimer();
    },
    onError: () => setError('Erreur lors du renvoi du code. Veuillez réessayer.'),
  });

  const startTimer = () => setResendTimer(RESEND_OTP_TIMER_SECONDS);

  const handleLogin = (event) => {
    event.preventDefault();

    if (!validateEmail(email)) {
      setError(Locales.auth.errors.invalid_email);
      setPwd('');
      setEmail('');
      return;
    }

    setError('');
    logUser();
  };

  const toggleDisplayPassword = () => setDisplayPwd((val) => !val);

  const toggleLegalNoticeOpen = () => setLegalNoticeOpen((val) => !val);

  const acceptCgu = () => {
    storage.set(CONST_LEGAL_ACCEPTED, true);
    const acceptanceDateTime = formatDate(new Date(), "EEEE d MMMM yyyy 'à' HH:mm:ss");
    storage.set(CONST_LEGAL_ACCEPTANCE_DATE, acceptanceDateTime);
    toggleLegalNoticeOpen();
  };

  // Auto-submit OTP when 6 characters are reached
  useEffect(() => {
    if (otpValue.length === 6) {
      verifyOTP();
    }
  }, [otpValue, verifyOTP]);

  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  return (
    <AuthPageLayout>
      {isLegalAccepted && (
        <AuthCard className="max-w-[448px]">
          <div className="flex flex-col space-y-6">
            <div className="text-left">
              <h1 className="text-2xl font-semibold leading-[1.3] tracking-[-0.55px] text-[#21312a] max-[520px]:text-[22px]">
                {title}
              </h1>
              <p className="mt-2 text-sm leading-[1.6] text-[#62736a] [&_strong]:mt-1 [&_strong]:block [&_strong]:font-semibold [&_strong]:text-[#304f3b]">
                {description}
              </p>
            </div>

            {error && (
              <Alert variant="destructive" className="animate-slide-up flex items-center gap-1">
                <TriangleAlert className="h-4 w-4 text-red-600 mb-2" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {isLoginStep && (
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-1">
                  <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                    {Locales.base.field.email}
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder={Locales.base.field.placeholder.email}
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setError('');
                    }}
                    variant="hajj"
                    size="sm"
                    autoFocus
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                    {Locales.base.field.password}
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={displayPwd ? 'text' : 'password'}
                      placeholder="••••••••••••••••••"
                      value={pwd}
                      onChange={(e) => {
                        setPwd(e.target.value);
                        setError('');
                      }}
                      variant="hajj"
                      size="sm"
                    />
                    <button
                      type="button"
                      onClick={toggleDisplayPassword}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      {displayPwd ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                <div className="text-left">
                  <Link
                    to={Urls.front.password.forgot}
                    className="text-sm text-hajj-primary hover:text-hajj-secondary font-medium transition-colors"
                  >
                    {Locales.auth.forgot_pwd.subtitle}
                  </Link>
                </div>

                <Button
                  type="submit"
                  variant="hajj"
                  className="w-full py-[20px]"
                  size="sm"
                  disabled={isLoggingIn || isEmptyField}
                >
                  {isLoggingIn ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Connexion en cours...
                    </>
                  ) : (
                    Locales.auth.connect
                  )}
                </Button>
              </form>
            )}

            {!isLoginStep && (
              <div className="flex-1 flex flex-col justify-center">
                <div className="space-y-6 flex flex-col items-center justify-center w-full">
                  <OTPForm value={otpValue} onChange={setOtpValue} disabled={isVerifyingOTP} />

                  {isVerifyingOTP && (
                    <div className="flex items-center justify-center text-sm text-hajj-primary">
                      <Loader2 className="animate-spin mr-2 h-4 w-4" />
                      Vérification en cours...
                    </div>
                  )}

                  {!isVerifyingOTP && (
                    <div className="flex justify-between items-center w-full">
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => setStep(CONST_LOGIN)}
                        className="text-gray-600 hover:text-gray-800 transition-colors"
                      >
                        Retour
                      </Button>

                      {resendTimer > 0 ? (
                        <p className="text-xs text-[#61766a]">
                          Renvoyer le code dans <span className="font-medium">{resendTimer}s</span>
                        </p>
                      ) : (
                        <Button
                          variant="link"
                          size="sm"
                          onClick={() => resendOTP()}
                          disabled={isResending || isVerifyingOTP}
                          className="text-hajj-primary hover:text-hajj-secondary transition-colors"
                        >
                          Renvoyer le code
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {isLoginStep && (
              <div className="space-y-2 text-center text-sm text-[#65766c]">
                <p>
                  Pas encore inscrit ?{' '}
                  <Link to={Urls.register} className="text-hajj-primary hover:text-hajj-secondary font-medium">
                    Créez votre compte
                  </Link>
                </p>

                <p className="mt-[19px] border-t border-[#e4eae5] pt-[18px] text-left text-xs leading-[1.7] text-[#738279]">
                  {Locales.formatString(Locales.auth.legals.auth_disclaimer, {
                    cgu: (
                      <a
                        href={Urls.cgu}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-hajj-primary hover:text-hajj-secondary font-medium transition-colors"
                      >
                        {Locales.auth.legals.cgu}
                      </a>
                    ),
                    mentions: (
                      <a
                        href={Urls.legal_mention}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-hajj-primary hover:text-hajj-secondary font-medium transition-colors"
                      >
                        {Locales.auth.legals.mentions}
                      </a>
                    ),
                  })}
                </p>
              </div>
            )}
          </div>
        </AuthCard>
      )}

      {legalNoticeOpen && !isLegalAccepted && <LegalNotice cta={acceptCgu} />}
    </AuthPageLayout>
  );
};

export default Login;
